import { CoreTypes, type Id, type ObjectComponent, type ObjectRecord, type Op, type Transaction } from '@vp/shared-types';
import type { CanvasDocument, TxContext } from './document';
import type { History } from './history';
import { ulid } from './ulid';
import { keysBetween } from './fractional';
import { boxBounds } from './geometry';
import * as cmd from './commands';

/**
 * Components: an object (often a group) made a master; its instances are copies that follow it.
 * Every part of an instance remembers the master part it mirrors (`componentPart`). When the master
 * changes, the instances take the change in the same undo step: style and data of each part, the
 * layout of the parts inside the instance (scaled to the instance's size), added and removed parts.
 * What the user changed on an instance part itself stays (`overrides`: `style.<key>`, `props.<key>`,
 * `geometry`), until "reset".
 */

const LOCAL_GEOMETRY = ['points', 'nodes', 'holes', 'path'];
const SYNC = 'component.sync';

export function componentOf(o: ObjectRecord | undefined): ObjectComponent | undefined {
  return o?.component as ObjectComponent | undefined;
}

/** The master root of a component id. */
export function masterOf(doc: CanvasDocument, componentId: string): ObjectRecord | undefined {
  for (const o of doc.allObjects()) {
    const c = componentOf(o);
    if (c?.role === 'master' && c.id === componentId) return o;
  }
  return undefined;
}

export function instancesOf(doc: CanvasDocument, componentId: string): ObjectRecord[] {
  return [...doc.allObjects()].filter((o) => {
    const c = componentOf(o);
    return c?.role === 'instance' && c.id === componentId;
  });
}

/** All masters on the canvas, by name. */
export function listComponents(doc: CanvasDocument): { id: string; name: string; master: ObjectRecord }[] {
  const out: { id: string; name: string; master: ObjectRecord }[] = [];
  for (const o of doc.allObjects()) {
    const c = componentOf(o);
    if (c?.role === 'master') out.push({ id: c.id, name: c.name || o.name || 'Component', master: o });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** The component root (master or instance) an object belongs to, walking up its groups. */
export function componentRoot(doc: CanvasDocument, id: Id): ObjectRecord | undefined {
  let o = doc.get(id);
  while (o) {
    if (componentOf(o)) return o;
    o = o.parentId ? doc.get(o.parentId) : undefined;
  }
  return undefined;
}

/** Make an object a master. Several objects are grouped first. Returns the master id. */
export function createComponent(tx: TxContext, ids: Id[], name: string): Id | null {
  const doc = tx.document;
  const roots = [...new Set(ids.map((id) => cmd.topAncestor(doc, id)))].filter((id) => doc.has(id));
  if (!roots.length) return null;
  const rootId = roots.length > 1 ? cmd.group(tx, roots) : roots[0];
  if (!rootId) return null;
  const root = doc.get(rootId)!;
  // a part of another component cannot become a master of its own
  for (const id of cmd.withDescendants(doc, [rootId])) {
    const o = doc.get(id)!;
    if (o.component || o.componentPart) tx.update(id, { component: undefined, componentPart: undefined, overrides: undefined } as Partial<ObjectRecord>);
  }
  tx.update(rootId, { component: { id: ulid(), role: 'master', name: name.trim().slice(0, 80) || root.name || 'Component' } } as Partial<ObjectRecord>);
  return rootId;
}

/** A new instance of a component with its top-left corner at `at`. Returns its root id. */
export function instantiateComponent(tx: TxContext, componentId: string, at: { x: number; y: number }): Id | null {
  const doc = tx.document;
  const master = masterOf(doc, componentId);
  if (!master) return null;
  const payload = cmd.serializeSelection(doc, [master.id]);
  if (!payload) return null;
  const before = new Set([...doc.allObjects()].map((o) => o.id));
  const roots = cmd.instantiatePayload(tx, payload, at);
  // the copies get layer keys in the payload's order: sorted by key they line up with it
  const copies = [...doc.allObjects()].filter((o) => !before.has(o.id)).sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
  payload.objects.forEach((src, i) => {
    const c = copies[i];
    if (!c) return;
    tx.update(c.id, { componentPart: src.id, overrides: undefined, component: src.id === master.id ? { id: componentId, role: 'instance' } : undefined } as Partial<ObjectRecord>);
  });
  return roots[0] ?? null;
}

/** An instance becomes plain objects. */
export function detachInstance(tx: TxContext, rootId: Id): void {
  for (const id of cmd.withDescendants(tx.document, [rootId])) tx.update(id, { component: undefined, componentPart: undefined, overrides: undefined } as Partial<ObjectRecord>);
}

/** Forget what the user changed on an instance; the next sync brings the master's look back. */
export function resetInstance(tx: TxContext, rootId: Id): void {
  for (const id of cmd.withDescendants(tx.document, [rootId])) {
    if ((tx.document.get(id)?.overrides as string[] | undefined)?.length) tx.update(id, { overrides: undefined } as Partial<ObjectRecord>);
  }
  syncInstance(tx, rootId);
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Bring one instance in line with its master (within an open transaction). */
export function syncInstance(tx: TxContext, rootId: Id): void {
  const doc = tx.document;
  const root = doc.get(rootId);
  const c = componentOf(root);
  if (!root || c?.role !== 'instance') return;
  const master = masterOf(doc, c.id);
  if (!master || master.type !== root.type) return;
  const masterIds = cmd.withDescendants(doc, [master.id]);
  const masterSet = new Set(masterIds);
  const parts = new Map<Id, ObjectRecord>();
  for (const id of cmd.withDescendants(doc, [rootId])) {
    const o = doc.get(id)!;
    const part = (o.componentPart as Id | undefined) ?? (id === rootId ? master.id : undefined);
    if (part && masterSet.has(part)) parts.set(part, o);
    else if (id !== rootId) tx.delete(id); // the part is gone from the master
  }
  const mb = boxBounds(master);
  const sx = mb.w > 0 ? root.w / mb.w : 1;
  const sy = mb.h > 0 ? root.h / mb.h : 1;
  const missing: ObjectRecord[] = [];
  for (const mid of masterIds) {
    const m = doc.get(mid)!;
    const p = parts.get(mid);
    if (!p) {
      missing.push(m);
      continue;
    }
    const ov = new Set((p.overrides as string[] | undefined) ?? []);
    const patch: Record<string, unknown> = {};
    // look: every style key the user did not change here
    const style = { ...p.style } as Record<string, unknown>;
    let styleChanged = false;
    for (const [k, v] of Object.entries(m.style)) {
      if (ov.has('style.' + k) || same(style[k], v)) continue;
      style[k] = structuredClone(v);
      styleChanged = true;
    }
    if (styleChanged) patch['style'] = style;
    // data: likewise, with outlines scaled to the instance
    const props = { ...p.props };
    let propsChanged = false;
    const scale = mid === master.id ? { x: 1, y: 1 } : { x: sx, y: sy };
    for (const [k, v] of Object.entries(m.props)) {
      if (ov.has('props.' + k) || (k === 'spans' && ov.has('props.text'))) continue;
      const nv = LOCAL_GEOMETRY.includes(k) ? scaleLocal(v, scale.x, scale.y) : v;
      if (same(props[k], nv)) continue;
      props[k] = structuredClone(nv);
      propsChanged = true;
    }
    for (const k of Object.keys(props)) {
      if (!(k in m.props) && !ov.has('props.' + k)) {
        delete props[k];
        propsChanged = true;
      }
    }
    if (propsChanged) patch['props'] = props;
    // layout of the parts inside the instance (the instance's own place and size are its own)
    if (mid !== master.id && !ov.has('geometry')) {
      const g = { x: root.x + (m.x - master.x) * sx, y: root.y + (m.y - master.y) * sy, w: m.w * sx, h: m.h * sy, rotation: m.rotation };
      for (const [k, v] of Object.entries(g)) if (Math.abs((p as unknown as Record<string, number>)[k] - v) > 1e-6) patch[k] = v;
    }
    if (m.name !== p.name && !ov.has('name')) patch['name'] = m.name;
    if (Object.keys(patch).length) tx.update(p.id, patch as Partial<ObjectRecord>);
  }
  // parts added to the master: copied in, in the master's layer order
  if (missing.length) {
    const keys = keysBetween(cmd.nextZ(doc), null, missing.length);
    missing
      .sort((a, b) => (a.z < b.z ? -1 : 1))
      .forEach((m, i) => {
        const parentPart = m.parentId ? (parts.get(m.parentId) ?? null) : null;
        const rec: ObjectRecord = {
          ...structuredClone(m),
          id: ulid(),
          parentId: parentPart ? parentPart.id : rootId,
          frameId: root.frameId,
          z: keys[i],
          x: root.x + (m.x - master.x) * sx,
          y: root.y + (m.y - master.y) * sy,
          w: m.w * sx,
          h: m.h * sy,
          props: Object.fromEntries(Object.entries(m.props).map(([k, v]) => [k, LOCAL_GEOMETRY.includes(k) ? scaleLocal(v, sx, sy) : structuredClone(v)])),
          locked: false,
          component: undefined,
          componentPart: m.id,
          overrides: undefined,
        } as ObjectRecord;
        tx.create(rec);
        parts.set(m.id, rec);
      });
  }
  if (root.type === CoreTypes.group) cmd.updateGroupBounds(tx, rootId);
}

function scaleLocal(v: unknown, sx: number, sy: number): unknown {
  if (sx === 1 && sy === 1) return v;
  if (!Array.isArray(v)) return v;
  if (v.length && Array.isArray(v[0])) return v.map((r) => scaleLocal(r, sx, sy));
  return v.map((n, i) => (typeof n === 'number' ? n * (i % 2 ? sy : sx) : n));
}

/**
 * Keeps instances following their masters: after every user change, the instances of the touched
 * masters are synced and the result is added to the same undo step. Changes the user makes on an
 * instance part are remembered as overrides. A copied master gets a component id of its own.
 */
export class ComponentSync {
  private unsub: () => void;

  constructor(
    private readonly doc: CanvasDocument,
    private readonly history: History,
  ) {
    this.unsub = doc.onCommit((tx, meta) => {
      if (meta.history || meta.replay || tx.label === SYNC || tx.label === 'recompute' || doc.readOnly) return;
      try {
        this.process(tx);
      } catch {
        // a broken component never blocks editing
      }
    });
  }

  dispose(): void {
    this.unsub();
  }

  private process(tx: Transaction): void {
    const doc = this.doc;
    const touched = new Set<Id>();
    const created = new Set<Id>();
    for (const op of tx.ops) {
      if (op.table !== 'objects') continue;
      const id = op.t === 'update' ? op.id : op.rec.id;
      touched.add(id);
      if (op.t === 'create') created.add(id);
    }
    if (!touched.size) return;
    const masters = new Set<string>();
    const overrides = new Map<Id, Set<string>>();
    const renamed: { id: Id; c: ObjectComponent }[] = [];
    for (const op of tx.ops) {
      if (op.table !== 'objects') continue;
      const id = op.t === 'update' ? op.id : op.rec.id;
      const o = doc.get(id);
      const deletedRec = op.t === 'delete' ? (op.rec as ObjectRecord) : undefined;
      // parent chain of a deleted part: its parent is still there
      const root = o ? componentRoot(doc, id) : deletedRec?.parentId ? componentRoot(doc, deletedRec.parentId) : undefined;
      const rc = componentOf(root);
      if (!root || !rc) continue;
      if (rc.role === 'master') {
        // a copy of a master (duplicate, paste): a component of its own
        if (created.has(root.id) && root.id === id && [...doc.allObjects()].some((x) => x.id !== root.id && componentOf(x)?.role === 'master' && componentOf(x)?.id === rc.id)) {
          renamed.push({ id: root.id, c: { ...rc, id: ulid() } });
          continue;
        }
        if (!created.has(root.id) || id !== root.id) masters.add(rc.id);
      } else if (op.t === 'update' && o && !created.has(id)) {
        // the user changed an instance part: remember what, unless the whole instance moved
        const before = op.before as Record<string, unknown>;
        const after = op.after as Record<string, unknown>;
        const keys = overrides.get(id) ?? new Set<string>();
        const master = masterOf(doc, rc.id);
        const mpart = master ? doc.get((o.componentPart as Id | undefined) ?? (id === root.id ? master.id : '')) : undefined;
        if (after['style'] && mpart) for (const k of Object.keys(after['style'] as object)) if (!same((after['style'] as Record<string, unknown>)[k], (before['style'] as Record<string, unknown> | undefined)?.[k]) && !same((after['style'] as Record<string, unknown>)[k], (mpart.style as Record<string, unknown>)[k])) keys.add('style.' + k);
        if (after['props'] && mpart) for (const k of Object.keys(after['props'] as object)) if (!LOCAL_GEOMETRY.includes(k) && !same((after['props'] as Record<string, unknown>)[k], (before['props'] as Record<string, unknown> | undefined)?.[k]) && !same((after['props'] as Record<string, unknown>)[k], mpart.props[k])) keys.add('props.' + k);
        if ('name' in after && mpart && after['name'] !== mpart.name) keys.add('name');
        const moved = ['x', 'y', 'w', 'h', 'rotation'].some((k) => k in after);
        if (moved && id !== root.id && !touched.has(root.id)) keys.add('geometry');
        if (keys.size) overrides.set(id, keys);
      }
    }
    if (!masters.size && !overrides.size && !renamed.length) return;
    // outside the undo stack; the result is appended to the user's step, so undo takes both back
    const t = doc.begin({ source: { kind: 'system' }, label: SYNC });
    let done: Transaction | null = null;
    try {
      for (const r of renamed) t.update(r.id, { component: r.c } as Partial<ObjectRecord>);
      for (const [id, keys] of overrides) {
        const o = doc.get(id);
        if (!o) continue;
        const cur = new Set((o.overrides as string[] | undefined) ?? []);
        const n = cur.size;
        for (const k of keys) cur.add(k);
        if (cur.size !== n) t.update(id, { overrides: [...cur] } as Partial<ObjectRecord>);
      }
      for (const cid of masters) for (const inst of instancesOf(doc, cid)) syncInstance(t, inst.id);
      done = t.commit({ history: true });
    } catch (e) {
      if (t.isOpen) t.rollback();
      throw e;
    }
    if (done && done.ops.length) this.history.amend(tx.id, done.ops as Op[]);
  }
}
